// Reference material attached to one learner activity.
//
// The instructor points at the reading, the syllabus extract or the spec the
// activity is built on; the text is pulled out in the browser and travels with
// the plan, so the AI Prompt page and any assistant that gets the export can
// work from the source rather than from a summary of it.

import React, { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight, ExternalLink, FileText, Link2, Loader2, Paperclip, Trash2 } from 'lucide-react';

import { pickFile } from '../../../utils/filePicker';
import {
  KIND_LABELS,
  MAX_TOTAL_REFERENCE_CHARS,
  REFERENCE_FILE_ACCEPT,
  createReferenceLink,
  formatFileSize,
  readReferenceFile
} from '../utils/referenceFiles';

const BLANK_LINK = { url: '', name: '', note: '' };

const ReferenceFileManager = ({ files = [], onChange, activityLabel, remainingCharBudget = MAX_TOTAL_REFERENCE_CHARS }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState('');
  // The link form, or null when it is closed.
  const [linkDraft, setLinkDraft] = useState(null);

  const handleAdd = async () => {
    setError('');
    const chosen = await pickFile({ accept: REFERENCE_FILE_ACCEPT, multiple: true });
    const list = Array.isArray(chosen) ? chosen : [chosen].filter(Boolean);
    if (!list.length) return;

    setBusy(true);
    const added = [];
    const problems = [];
    let budget = remainingCharBudget;

    for (const file of list) {
      try {
        // Sequential rather than Promise.all: a PDF is decompressed stream by
        // stream, and running several at once on a phone is how the tab dies.
        // eslint-disable-next-line no-await-in-loop
        const record = await readReferenceFile(file);
        if (record.charCount > budget) {
          problems.push(`"${record.name}" was not added — the plan is at its reference-material limit.`);
          continue;
        }
        budget -= record.charCount;
        added.push(record);
        if (record.note) problems.push(`${record.name}: ${record.note}`);
      } catch (readError) {
        problems.push(readError.message || `Could not read "${file.name}".`);
      }
    }

    setBusy(false);
    if (added.length) onChange([...files, ...added]);
    if (problems.length) setError(problems.join(' '));
  };

  const handleRemove = (id) => {
    setError('');
    if (expandedId === id) setExpandedId('');
    onChange(files.filter((file) => file.id !== id));
  };

  const handleAddLink = () => {
    const record = createReferenceLink(linkDraft);
    if (!record) {
      setError('That does not look like a web address. Links must start with http:// or https://.');
      return;
    }
    setError('');
    setLinkDraft(null);
    onChange([...files, record]);
  };

  return (
    <div className="mt-2 rounded border border-dashed border-gray-300 bg-gray-50 p-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1 text-xs font-semibold text-gray-600">
          <Paperclip size={12} />
          Reference material
          {files.length > 0 && <span className="font-normal text-gray-500">({files.length})</span>}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleAdd}
            disabled={busy}
            className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            title={`Attach reference material to ${activityLabel}`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
            {busy ? 'Reading…' : 'Add file'}
          </button>
          <button
            type="button"
            onClick={() => { setError(''); setLinkDraft((current) => (current ? null : BLANK_LINK)); }}
            className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700"
            title={`Attach a web link to ${activityLabel}`}
            aria-expanded={linkDraft !== null}
          >
            <Link2 size={12} />
            Add link
          </button>
        </div>
      </div>

      {linkDraft !== null && (
        <div className="mt-2 space-y-1.5 rounded border border-gray-200 bg-white p-2">
          <input
            type="url"
            autoFocus
            value={linkDraft.url}
            onChange={(event) => setLinkDraft({ ...linkDraft, url: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); handleAddLink(); }
              if (event.key === 'Escape') setLinkDraft(null);
            }}
            placeholder="https://example.com/reading"
            aria-label={`Web address for ${activityLabel}`}
            className="w-full rounded border border-gray-300 px-2 py-1 text-[11px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <input
            type="text"
            value={linkDraft.name}
            onChange={(event) => setLinkDraft({ ...linkDraft, name: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); handleAddLink(); }
              if (event.key === 'Escape') setLinkDraft(null);
            }}
            placeholder="Title (optional)"
            aria-label={`Link title for ${activityLabel}`}
            className="w-full rounded border border-gray-300 px-2 py-1 text-[11px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <textarea
            value={linkDraft.note}
            onChange={(event) => setLinkDraft({ ...linkDraft, note: event.target.value })}
            placeholder="What is at this link, and what should be taken from it? (optional — this is what the assistant reads)"
            rows={2}
            aria-label={`Link notes for ${activityLabel}`}
            className="w-full rounded border border-gray-300 px-2 py-1 text-[11px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setLinkDraft(null)}
              className="rounded px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            {/* "Add", not "Add link" — the button that opens this form is
                already called that, and two controls with the same name is a
                puzzle for anyone reading the page rather than looking at it. */}
            <button
              type="button"
              onClick={handleAddLink}
              disabled={!linkDraft.url.trim()}
              className="rounded bg-blue-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {files.length === 0 && !error && linkDraft === null && (
        <p className="mt-1 text-[11px] leading-snug text-gray-500">
          Attach .txt, .md, .json, .pdf or .docx source material, or a web link. What you attach
          is included in the saved plan, the HTML export and the hand-off to the AI Prompt page.
        </p>
      )}

      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((file) => {
            const expanded = expandedId === file.id;
            const isLink = file.kind === 'link';
            return (
              <li key={file.id} className="rounded border border-gray-200 bg-white">
                <div className="flex items-center gap-2 px-2 py-1">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? '' : file.id)}
                    className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                    title={isLink ? 'Show the address and notes' : (file.text ? 'Show the extracted text' : 'No text could be extracted')}
                    aria-expanded={expanded}
                  >
                    {expanded ? <ChevronDown size={12} className="flex-shrink-0 text-gray-400" /> : <ChevronRight size={12} className="flex-shrink-0 text-gray-400" />}
                    {isLink
                      ? <Link2 size={12} className="flex-shrink-0 text-blue-500" />
                      : <FileText size={12} className="flex-shrink-0 text-gray-400" />}
                    <span className="truncate text-[11px] font-medium text-gray-800">{file.name}</span>
                    <span className="flex-shrink-0 text-[10px] text-gray-500">
                      {KIND_LABELS[file.kind] || 'File'}
                      {!isLink && file.size ? ` · ${formatFileSize(file.size)}` : ''}
                      {isLink
                        ? (file.charCount ? ' · with notes' : '')
                        : (file.charCount ? ` · ${file.charCount.toLocaleString()} chars` : ' · no text')}
                      {file.truncated ? ' · truncated' : ''}
                    </span>
                  </button>
                  {isLink && file.url && (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                      title={`Open ${file.url}`}
                      aria-label={`Open ${file.name} in a new tab`}
                    >
                      <ExternalLink size={12} />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemove(file.id)}
                    className="flex-shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    title={`Remove ${file.name}`}
                    aria-label={`Remove ${file.name}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                {expanded && (
                  <div className="border-t border-gray-100 px-2 py-1">
                    {file.note && (
                      <p className="mb-1 text-[11px] italic text-amber-700">{file.note}</p>
                    )}
                    {isLink && (
                      <p className="mb-1 break-all text-[11px] text-blue-600">{file.url}</p>
                    )}
                    {file.text ? (
                      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-snug text-gray-700">
                        {file.text}
                      </pre>
                    ) : (
                      <p className="text-[11px] text-gray-500">
                        {isLink
                          ? 'No notes were added. The address travels with the plan either way.'
                          : 'No text was extracted. The file is still listed in the plan by name and type.'}
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div className="mt-2 flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ReferenceFileManager;
